import { getCookieFromHeader } from "./cookie";

export type CookieConsentValue = "agreed" | "rejected";
export type CookieConsentSource = "signed_out";

export const CONSENT_COOKIE_MAX_AGE_SECONDS = 6 * 30 * 24 * 60 * 60;
export const COOKIE_COUNTRY_OVERRIDE_QUERY_PARAM = "cookieCountry";
export const COOKIE_REGION_OVERRIDE_QUERY_PARAM = "cookieRegion";
export const COOKIE_BANNER_LAYER = "chatgpt_cookie_banner_layer";
export const ADS_COOKIE_BANNER_ROLLOUT_CONFIG = "bazaar.tapestry.ads_for_ads_cookie_banner";
export const DEFAULT_COOKIE_BANNER_ROLLOUT_COUNTRIES = [
  "BR",
  "CA",
  "ID",
  "IN",
  "JP",
  "MX",
  "SG",
  "ZA",
] as const;
export const COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE =
  "chatgpt_cookie_personalization_choices_enabled";

const CLOUDFLARE_UNKNOWN_COUNTRY_CODES = new Set(["T1", "XX"]);

const EEA_COUNTRIES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GF",
  "GP",
  "GR",
  "HU",
  "IS",
  "IE",
  "IT",
  "LV",
  "LI",
  "LT",
  "LU",
  "MF",
  "MQ",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RE",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "YT",
]);

export const COUNTRIES_REQUIRING_COOKIE_BANNER = new Set([
  ...EEA_COUNTRIES,
  "AD",
  "AI",
  "AL",
  "AW",
  "AX",
  "BL",
  "BM",
  "BQ",
  "BV",
  "CH",
  "CO",
  "CW",
  "EC",
  "FK",
  "FO",
  "GB",
  "GG",
  "GI",
  "GL",
  "GS",
  "IC",
  "IM",
  "IO",
  "JE",
  "KR",
  "KY",
  "MC",
  "MD",
  "ME",
  "MK",
  "MS",
  "NC",
  "PF",
  "PM",
  "PN",
  "RS",
  "SH",
  "SJ",
  "SM",
  "SX",
  "TC",
  "TF",
  "TH",
  "VA",
  "VG",
  "VN",
  "WF",
]);

const CANADIAN_SUBDIVISION_CODES = new Set([
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
]);

export function getCookieConsentFromHeader(
  cookieHeader: string,
  key: "analytics_consent" | "marketing_consent" | "personalization_consent",
): CookieConsentValue | undefined {
  const consent = getCookieFromHeader(cookieHeader, key);
  return consent === "agreed" || consent === "rejected" ? consent : undefined;
}

export function getCookieConsentSourceFromHeader(
  cookieHeader: string,
  key: "analytics_consent_source" | "marketing_consent_source" | "personalization_consent_source",
): CookieConsentSource | undefined {
  return getCookieFromHeader(cookieHeader, key) === "signed_out" ? "signed_out" : undefined;
}

export function getCookieBannerDismissedFromHeader(cookieHeader: string): boolean {
  return getCookieFromHeader(cookieHeader, "cookie_banner_dismissed") === "true";
}

export function normalizeCountryCode(countryCode?: string): string | undefined {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (
    !normalizedCountryCode ||
    !/^[A-Z]{2}$/.test(normalizedCountryCode) ||
    CLOUDFLARE_UNKNOWN_COUNTRY_CODES.has(normalizedCountryCode)
  ) {
    return undefined;
  }

  return normalizedCountryCode;
}

function normalizeCanadianSubdivisionCode(regionCode?: string): string | undefined {
  const normalizedRegionCode = regionCode?.trim().toUpperCase();
  if (!normalizedRegionCode) {
    return undefined;
  }

  const subdivisionCode =
    normalizedRegionCode.startsWith("CA-") ?
      normalizedRegionCode.slice("CA-".length)
    : normalizedRegionCode;
  if (!subdivisionCode || !CANADIAN_SUBDIVISION_CODES.has(subdivisionCode)) {
    return undefined;
  }

  return subdivisionCode;
}

function normalizeSubdivisionCode(regionCode?: string): string | undefined {
  const normalizedRegionCode = regionCode?.trim().toUpperCase();
  if (!normalizedRegionCode) {
    return undefined;
  }

  const subdivisionMatch = normalizedRegionCode.match(/^(?:[A-Z]{2}-)?([A-Z]{2})$/);
  if (!subdivisionMatch) {
    return undefined;
  }

  return subdivisionMatch[1];
}

export function requiresCookieBanner(countryCode?: string, regionCode?: string): boolean {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  if (!normalizedCountryCode) {
    return true;
  }

  if (normalizedCountryCode === "CA") {
    const subdivisionCode = normalizeCanadianSubdivisionCode(regionCode);
    return subdivisionCode == null || subdivisionCode === "QC";
  }

  return COUNTRIES_REQUIRING_COOKIE_BANNER.has(normalizedCountryCode);
}

export function isCookieBannerRolloutCountry(
  countryCode: string | undefined,
  rolloutCountries: unknown,
): boolean {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  return (
    normalizedCountryCode != null &&
    Array.isArray(rolloutCountries) &&
    rolloutCountries.some(
      (rolloutCountry) =>
        typeof rolloutCountry === "string" &&
        normalizeCountryCode(rolloutCountry) === normalizedCountryCode,
    )
  );
}

export function resolveCookieBannerRolloutCountries(
  configuredRolloutCountries: unknown,
): readonly string[] {
  if (
    !Array.isArray(configuredRolloutCountries) ||
    configuredRolloutCountries.some(
      (countryCode) => typeof countryCode !== "string" || normalizeCountryCode(countryCode) == null,
    )
  ) {
    return DEFAULT_COOKIE_BANNER_ROLLOUT_COUNTRIES;
  }

  return configuredRolloutCountries;
}

export function hasUnansweredApplicableConsent({
  analyticsConsent,
  marketingConsent,
  personalizationConsent,
  includePersonalizationConsent,
}: {
  analyticsConsent?: CookieConsentValue;
  marketingConsent?: CookieConsentValue;
  personalizationConsent?: CookieConsentValue;
  includePersonalizationConsent: boolean;
}): boolean {
  return (
    analyticsConsent == null ||
    marketingConsent == null ||
    (includePersonalizationConsent && personalizationConsent == null)
  );
}

export function shouldShowCookieBanner({
  isAuthenticated,
  isBannerDismissed,
  isConsentResolved,
  isConsentRequired,
  isBannerExperimentCountry: isBannerExperimentCountryInput,
  analyticsConsent,
  marketingConsent,
  personalizationConsent,
  includePersonalizationConsent,
}: {
  isAuthenticated: boolean;
  isBannerDismissed: boolean;
  isConsentResolved: boolean;
  isConsentRequired: boolean;
  isBannerExperimentCountry: boolean;
  analyticsConsent?: CookieConsentValue;
  marketingConsent?: CookieConsentValue;
  personalizationConsent?: CookieConsentValue;
  includePersonalizationConsent: boolean;
}): boolean {
  if (isAuthenticated || isBannerDismissed || !isConsentResolved) {
    return false;
  }

  if (!isConsentRequired && !isBannerExperimentCountryInput) {
    return false;
  }

  return hasUnansweredApplicableConsent({
    analyticsConsent,
    marketingConsent,
    personalizationConsent,
    includePersonalizationConsent,
  });
}

export function getDevCookieCountryOverride(searchParams: URLSearchParams): string | undefined {
  if (!import.meta.env.DEV) {
    return undefined;
  }

  return normalizeCountryCode(searchParams.get(COOKIE_COUNTRY_OVERRIDE_QUERY_PARAM) ?? undefined);
}

export function getDevCookieRegionOverride(searchParams: URLSearchParams): string | undefined {
  if (!import.meta.env.DEV) {
    return undefined;
  }

  return normalizeSubdivisionCode(
    searchParams.get(COOKIE_REGION_OVERRIDE_QUERY_PARAM) ?? undefined,
  );
}
